import React, { useState, useEffect } from 'react'
import { getLoggingService, getLogSettings, type MessageActivityLog } from 'edge-stream-js'
import { HooksDetailedIcon } from '@edge-stream/app-pages-react'

// Wrapper component to add styling to the imported icon
function HooksIcon() {
  return (
    <span style={{ verticalAlign: 'text-bottom', marginRight: '8px', display: 'inline-block' }}>
      <HooksDetailedIcon />
    </span>
  )
}

type ViewMode = 'live' | 'settings'

const statusColors: Record<string, string> = {
  success: '#27ae60',
  failed: '#e74c3c',
  pending: '#f39c12',
}

export interface HooksMonitorProps {
  /** Custom class name for the container */
  className?: string
  /** Custom styles for the container */
  style?: React.CSSProperties
  /** Enable/disable settings tab */
  showSettings?: boolean
  /** Polling interval in ms */
  pollInterval?: number
  /** Max logs to retrieve */
  maxLogs?: number
}

/**
 * HooksMonitor - Real-time monitoring of hook execution in EdgeStream pipelines
 *
 * Displays:
 * - Statistics on total hooks, success rate, failures
 * - Real-time list of hook executions with filtering
 * - Detailed execution information including timing and errors
 * - Optional settings tab for enabling/disabling hook logging
 *
 * Usage:
 * ```tsx
 * import { HooksMonitor } from '@edge-stream/observability-react'
 *
 * <HooksMonitor
 *   showSettings={true}
 *   pollInterval={500}
 *   maxLogs={100}
 * />
 * ```
 */
export function HooksMonitor({
  className = 'page-container',
  style,
  showSettings = true,
  pollInterval = 500,
  maxLogs = 100,
}: HooksMonitorProps): React.ReactElement {
  const loggingService = getLoggingService()
  const logSettings = getLogSettings()

  const [viewMode, setViewMode] = useState<ViewMode>('live')
  const [logs, setLogs] = useState<MessageActivityLog[]>([])
  const [selectedLog, setSelectedLog] = useState<MessageActivityLog | null>(null)
  const [filter, setFilter] = useState<string>('')
  const [hookLoggingEnabled, setHookLoggingEnabled] = useState(
    logSettings.isHookLoggingEnabled()
  )

  // Refresh logs periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const allLogs = loggingService.getRecentLogs(maxLogs)
      setLogs(allLogs || [])
    }, pollInterval)
    return () => clearInterval(interval)
  }, [loggingService, pollInterval, maxLogs])

  const handleToggleHookLogging = () => {
    const newState = !hookLoggingEnabled
    setHookLoggingEnabled(newState)
    // Toggle hook logging in settings
  }

  // Extract all hook logs from message logs
  const allHookLogs = logs.flatMap((msg) => msg.hookLogs || [])

  const filteredHooks = filter
    ? allHookLogs.filter((log) => log.hookName?.toLowerCase().includes(filter.toLowerCase()))
    : allHookLogs

  const stats = {
    total: allHookLogs.length,
    success: allHookLogs.filter((l) => l.status === 'success').length,
    failed: allHookLogs.filter((l) => l.status === 'error').length,
  }

  return (
    <div className={className} style={style}>
      <div className="page-header" style={{ marginBottom: '2px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center' }}><HooksIcon />Hook Execution Pipeline</h2>
        <p className="page-description">
          Real-time monitoring of hook execution through the message processing pipeline
        </p>
      </div>

      {/* Tabs */}
      {showSettings && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            paddingBottom: '10px',
            borderBottom: '1px solid var(--border)',
            marginBottom: '20px',
          }}
        >
          <button
            onClick={() => setViewMode('live')}
            style={{
              height: '36px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              background: viewMode === 'live' ? 'var(--color-brand, #427A43)' : 'transparent',
              color: viewMode === 'live' ? 'white' : 'var(--text-primary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Live Monitor
          </button>
          <button
            onClick={() => setViewMode('settings')}
            style={{
              height: '36px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              background: viewMode === 'settings' ? 'var(--color-brand, #427A43)' : 'transparent',
              color: viewMode === 'settings' ? 'white' : 'var(--text-primary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Settings
          </button>
        </div>
      )}

      {viewMode === 'live' && (
        <div>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <div className="stat-card">
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Total Hooks</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>{stats.total}</p>
            </div>
            <div className="stat-card">
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Success</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#27ae60' }}>{stats.success}</p>
            </div>
            <div className="stat-card">
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Failed</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#e74c3c' }}>{stats.failed}</p>
            </div>
          </div>

          {/* Filter */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Filter by hook name..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input"
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* Hook Logs */}
          <div
            style={
              filteredHooks.length === 0
                ? { background: 'rgba(15, 23, 42, 0.6)', border: '1px dashed rgba(58, 140, 69, 0.35)', borderRadius: '12px', overflow: 'hidden' }
                : { background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', borderRadius: '12px', overflow: 'hidden' }
            }
          >
            {filteredHooks.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
                No hook executions recorded. Messages flow through hooks when received.
              </div>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {filteredHooks.map((log, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedLog(logs.find((m) => m.hookLogs?.includes(log)) || null)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border)',
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                      cursor: 'pointer',
                      background: selectedLog?.hookLogs?.includes(log) ? 'var(--bg-secondary)' : 'transparent',
                      transition: 'background 0.2s',
                    }}
                  >
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: statusColors[log.status] || '#999',
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '80px' }}>
                      Now
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px', minWidth: '120px' }}>{log.hookName}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1 }}>
                      {log.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail View */}
          {selectedLog && (
            <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#f8fafc' }}>Message Hooks Summary</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.6 }}>
                <div><strong>Message ID:</strong> {selectedLog.messageId}</div>
                <div><strong>Topic:</strong> {selectedLog.topic}</div>
                <div><strong>Hooks Executed:</strong> {selectedLog.hookLogs?.length || 0}</div>
                <div style={{ marginTop: '12px' }}>
                  <strong>Hook Details:</strong>
                  {selectedLog.hookLogs?.map((h, idx) => (
                    <div key={idx} style={{ marginLeft: '12px', marginTop: '8px', padding: '8px', background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(58, 140, 69, 0.25)', borderRadius: '8px' }}>
                      <div>{h.hookName} - {h.status}</div>
                      {h.executionTime && <div>Time: {h.executionTime}ms</div>}
                      {h.error && <div style={{ color: '#e74c3c' }}>Error: {h.error?.message}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showSettings && viewMode === 'settings' && (
        <div style={{ maxWidth: '500px' }}>
          <div className="activity-settings-card">
            <h4 style={{ margin: '0 0 16px 0', color: '#f8fafc' }}>Hook Logging Settings</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={hookLoggingEnabled} onChange={handleToggleHookLogging} />
                <span>Enable hook logging</span>
              </label>
              <p style={{ margin: '16px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                Hook logging tracks when hooks execute, including their status, duration, and any errors.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
